<script setup>
import ButtonSave from "@/components/common/button/ButtonSave.vue";
import {computed, onMounted, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import {
    getAvailableActionsForRoute,
    getMenu,
    getPermissionsManagementById,
    savePermissionManagement
} from "@/services/api/permissionManagement.js";

import {handleError} from "@/utils/errors.js";
import {getCorp, getCorpUsers, listCorps} from "@/services/api/corporation.js";
import {useRoute, useRouter} from "vue-router";
import BasePage from "@/components/Layouts/BasePage.vue";
import EntityForm from "@/components/common/Form.vue";
import {PermissionManagementEntity} from "@/entities/PermissionManagementEntity.js";
import {buildPayloadFromEntity, searchAndSetOptions} from "@/utils/index.js";
import {getManufacturer} from "@/services/api/manufacturer.js";
import {getPOPs} from "@/services/api/POP.js";
import {getPools} from "@/services/api/pool.js";
import {getAttributeLayout} from "@/services/api/attributeLayout.js";
import { configureSettingsEntityRoutes } from "@/utils/settingsRoutes.js";

const loading = ref(false);
const { t } = useI18n();
const tab = ref('group');
const isFetching = ref(false);
const router = useRouter();
const route = useRoute();
const permissionManagementEntity = configureSettingsEntityRoutes(new PermissionManagementEntity(), route);
const entity = ref(permissionManagementEntity);
const isEdit = computed(() => !!route.params.id)
const isMounted = ref(false);
const actions = ref([]);
const actionsPermission = ref(null);
const havePermission = ref(false);
const form = ref(null);
const formDataStep1 = ref(entity.value.fieldsStep1);
const formDataStep2 = ref(entity.value.fieldsStep2);
const id = route.params.id;

const fetchMenu = async () => {
    try {
        const idWorkgroup = isEdit.value ? route.params.id : '';
        const res = await getMenu({
            corpId: selectedCorpId.value,
            idWorkgroup: idWorkgroup
        });

        actions.value = res ? transformMenuData(res) : [];
        if (isEdit.value) {
            formDataStep1.value.permissions = extractAllowedIds(actions.value);
        }
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    }
};

const setPermissionsOptions = () => {
    const permissionsField = entity.value.fieldsStep1.find(f => f.key === "permissions");

    if (permissionsField) {
        permissionsField.options = actions.value;
    }
};

watch(actions, (newActions) => {
    if (newActions?.length) {
        setPermissionsOptions();

        if (isEdit.value && formDataStep1.value.permissions) {
            formDataStep1.value = {
                ...formDataStep1.value,
                permissions: [...formDataStep1.value.permissions]
            };
        }
    }
});

const selectedCorpId = computed(() => formDataStep1.value?.corp_id?.value);

watch(selectedCorpId, async (corpId) => {
    actions.value = [];

    const permissionsField = entity.value.fieldsStep1.find(f => f.key === "permissions");
    if (permissionsField) {
        permissionsField.options = [];
    }

    if (corpId) {
        await fetchMenu();
    }
});

const extractAllowedIds = (nodes) => {
    let allowedIds = [];

    nodes.forEach(node => {
        if (node.allow === true) {
            allowedIds.push(node.id);
        }

        if (node.children) {
            allowedIds = allowedIds.concat(extractAllowedIds(node.children));
        }
    });
    return allowedIds;
};

const transformMenuData = (menuItems, parentPath = '') => {
    return menuItems.map((item, index) => {
        const baseId = item.action_id || item.url || `${parentPath}/${item.title}`;

        const transformedItem = {
            name: item.title,
            id: baseId, // 🔥 NUNCA usar Math.random
            children: item.menu && item.menu.length > 0
                ? transformMenuData(item.menu, baseId)
                : undefined,
            allow: item.allow,
            title: item.title
        };

        if (!transformedItem.children) {
            delete transformedItem.children;
        }

        return transformedItem;
    });
};

const extractLeafIds = (nodes) => {
    let leafIds = [];

    nodes.forEach(node => {
        if (!node.children || node.children.length === 0) {
            leafIds.push(node.id);
        } else {
            leafIds = leafIds.concat(extractLeafIds(node.children));
        }
    });

    return leafIds;
};

async function handleSearch({ handler, query }) {

    const handlers = {
        handleCorpList: () =>
            searchAndSetOptions({
                key: "corp_id",
                apiFn: listCorps,
                query,
                entity,
                isFetching,
                formName: "fieldsStep1",
                mapFn: item => ({
                    value: item.id,
                    label: item.companyName,
                }),
                searchParam: "companyName"
            }),

        handleSearchUsersToSave: () =>
            searchAndSetOptions({
                key: "users",
                apiFn: getCorpUsers,
                formName: "fieldsStep2",
                query,
                entity,
                isFetching,
                mapFn: item => ({
                    value: item.id,
                    label: item.email,
                }),
                searchParam: "email"
            }),
    };

    if (handlers[handler]) {
        await handlers[handler]();
    } else {
        console.warn(`Handler não encontrado: ${handler}`);
    }
}

const handleSubmit = async () => {
    const { valid } = await form.value.validate();
    if (!valid) {
        return message.error(t('common.messages.errors.400'));
    }

    loading.value = true;

    try {
        formDataStep1.value.id = id ?? null;
        const payloadStep1 = buildPayloadFromEntity({entity: entity.value, data: formDataStep1.value, formName: "fieldsStep1"});
        const payloadStep2 = buildPayloadFromEntity({entity: entity.value, data: formDataStep2.value, formName: "fieldsStep2"});
        payloadStep1.id = formDataStep1.value.id ?? null;

        await savePermissionManagement({...payloadStep1, ...payloadStep2});
        await router.push(entity.value.pages.list.route?.at(-1));
        message.success(t("common.messages.success.save"));
    } catch (error) {
        console.error(error);
        message.error(await handleError(error.response, t, formDataStep1.value.name));
    } finally {
        loading.value = false
    }
}
onMounted(async () => {
    try {
        isMounted.value = false;

        const corp = await getCorp();
        const corpField = entity.value.fieldsStep1.find(f => f.key === "corp_id");

        if (corp && corpField) {
            corpField.required = false;
            corpField.visible = false;
        }

        actionsPermission.value = await getAvailableActionsForRoute("Resources/Settings/Permission Management");

        if (actionsPermission.value?.view?.length) {
            havePermission.value = true;
        } else {
            message.error(t("common.messages.errors.403"));
        }

        if (isEdit.value) {
            const response = await getPermissionsManagementById({ id: route.params.id });
            const data = response.data;

            data.permissions = data.permissions || [];

            if (data.corp?.id) {
                data.corp_id = {
                    value: data.corp.id,
                    title: data.corp.company_name
                };
            }

            data.users = Object.values(data.users || {}).map(user => ({
                title: user.email,
                value: user.id
            }));

            Object.assign(formDataStep1.value, data);
            Object.assign(formDataStep2.value, data);

        } else if (corp?.id) {
            formDataStep1.value.corp_id = {
                value: corp.id,
                title: corp.company_name
            };
        }

        if (formDataStep1.value?.corp_id?.value) {
            await fetchMenu();
        }

    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    } finally {
        isMounted.value = true;
    }
});

</script>

<template>
    <BasePage :loading-mounted="isMounted" :entity="entity">
        <v-card class="bg-white settings-card trezo-card">
            <v-tabs v-model="tab" class="mb-5">
                <v-tab class="text-dark router-link-active  me-4" value="group">
                    {{ t('components.settings.permissionManagement.pageTitle') }}
                </v-tab>
                <v-tab class="text-dark" value="users">
                    {{ t('components.settings.permissionManagement.linkedUsers') }}
                </v-tab>
            </v-tabs>
            <v-window v-model="tab">
                <v-window-item value="group">
                    <v-col md="6">
                        <EntityForm :entity="entity" fieldsToUse="fieldsStep1" ref="form"  v-model="formDataStep1"  @search="handleSearch"  />
                    </v-col>
                </v-window-item>
                <v-window-item value="users">
                    <EntityForm :entity="entity" fieldsToUse="fieldsStep2" ref="form"  v-model="formDataStep2" @search="handleSearch" />
                </v-window-item>
            </v-window>
            <div class="form-btn-box" v-if="actionsPermission?.edit">
                <ButtonSave  :loading="loading" @click="handleSubmit"  />
            </div>
        </v-card>
    </BasePage>
</template>

<style lang="scss" scoped>
.settings-card {
    margin-bottom: 10px;
    .v-tab.v-tab.v-btn  {
        max-height: 44px;
    }
    button {
        height: auto;
        min-width: auto;
        font-weight: 500;
        border-radius: 7px!important;
        padding: 10.5px 15px;
        color: var(--primaryColor);
        background-color: transparent;
        border: 1px solid var(--primaryColor);
        display: inline-block;
        text-decoration: none;
        letter-spacing: normal;
        text-transform: none;
        &.v-tab.v-tab--selected,
        &:hover {
            color: var(--whiteColor);
            background-color: var(--primaryColor);
        }
    }
}
</style>
